import { noiseBurst, tone, vary, type Voice } from './synth';

/**
 * The sound catalogue.
 *
 * Each entry is a recipe rather than a file. To replace one with a real
 * recording, drop the audio in `src/assets/audio/`, import it, and change that
 * entry to `{ file: theImport }`. Nothing at the call sites changes, because
 * they only ever name a sound.
 *
 * Design brief, matching the rest of the game: struck wood, iron, leather and
 * parchment. Nothing chimes, nothing sparkles, nothing announces itself. UI
 * sounds sit low and short so they can fire hundreds of times without grating.
 */
export type SoundName =
  | 'hover'
  | 'click'
  | 'clickSoft'
  | 'open'
  | 'close'
  | 'confirm'
  | 'cancel'
  | 'error'
  | 'notify'
  | 'build'
  | 'buildComplete'
  | 'train'
  | 'march'
  | 'victory'
  | 'defeat'
  | 'coin'
  | 'select'
  | 'enterVillage'
  | 'leaveVillage';

export type SoundChannel = 'ui' | 'game';

export interface SoundSpec {
  channel: SoundChannel;
  /** Relative loudness, before the channel and master volumes. */
  gain: number;
  /** Minimum milliseconds between plays, so rapid triggers do not stack. */
  throttleMs?: number;
  render: (voice: Voice) => void;
}

export const SOUNDS: Record<SoundName, SoundSpec> = {
  /** Barely there. A fingertip brushing leather. */
  hover: {
    channel: 'ui',
    gain: 0.1,
    throttleMs: 40,
    render: (v) => noiseBurst(v, { duration: 0.03, gain: 0.25, frequency: vary(2600), q: 0.7, type: 'highpass' }),
  },

  /** A knuckle on an oak table: a click of wood with a little body. */
  click: {
    channel: 'ui',
    gain: 0.5,
    throttleMs: 30,
    render: (v) => {
      noiseBurst(v, { duration: 0.05, gain: 0.5, frequency: vary(1500), q: 1.4, sweepTo: 500 });
      tone(v, { frequency: vary(190), duration: 0.06, gain: 0.16, type: 'triangle', slideTo: 120 });
    },
  },

  clickSoft: {
    channel: 'ui',
    gain: 0.32,
    throttleMs: 30,
    render: (v) => noiseBurst(v, { duration: 0.035, gain: 0.4, frequency: vary(1900), q: 1.2 }),
  },

  /** Parchment unrolling. Noise sweeping upward, no pitch at all. */
  open: {
    channel: 'ui',
    gain: 0.42,
    render: (v) => noiseBurst(v, { duration: 0.22, gain: 0.3, frequency: 700, q: 0.6, sweepTo: 2600, curve: 0.7 }),
  },

  close: {
    channel: 'ui',
    gain: 0.42,
    render: (v) => noiseBurst(v, { duration: 0.18, gain: 0.3, frequency: 2400, q: 0.6, sweepTo: 600, curve: 0.8 }),
  },

  /** Two ascending wooden knocks. Reads as "done" without chiming. */
  confirm: {
    channel: 'ui',
    gain: 0.5,
    render: (v) => {
      noiseBurst(v, { duration: 0.05, gain: 0.4, frequency: 1400, q: 1.5 });
      tone(v, { frequency: 240, duration: 0.07, gain: 0.14, type: 'triangle' });
      tone(v, { frequency: 330, duration: 0.09, gain: 0.13, type: 'triangle', delay: 0.075 });
    },
  },

  cancel: {
    channel: 'ui',
    gain: 0.42,
    render: (v) => {
      noiseBurst(v, { duration: 0.06, gain: 0.35, frequency: 900, q: 1.1, sweepTo: 380 });
      tone(v, { frequency: 200, duration: 0.09, gain: 0.12, type: 'triangle', slideTo: 140 });
    },
  },

  /**
   * Something happened that you did not ask for.
   *
   * Overridden by a real recording in assets/audio/effects/notify.mp3; this is
   * the fallback if that file is ever missing. Two rising notes, quiet: it
   * must be noticeable while you are looking elsewhere and must not startle
   * you when you are not.
   */
  notify: {
    channel: 'ui',
    gain: 0.4,
    render: (v) => {
      tone(v, { frequency: 660, duration: 0.09, gain: 0.12, type: 'triangle' });
      tone(v, { frequency: 880, duration: 0.12, gain: 0.11, type: 'triangle', delay: 0.08 });
    },
  },

  /** A dull, dead thud. Deliberately unpleasant, deliberately not a buzzer. */
  error: {
    channel: 'ui',
    gain: 0.55,
    render: (v) => {
      noiseBurst(v, { duration: 0.12, gain: 0.4, frequency: 320, q: 0.8, type: 'lowpass' });
      tone(v, { frequency: 98, duration: 0.16, gain: 0.2, type: 'square', slideTo: 72 });
    },
  },

  /** Chisel on stone: two quick taps, the second harder. */
  build: {
    channel: 'game',
    gain: 0.5,
    render: (v) => {
      noiseBurst(v, { duration: 0.05, gain: 0.35, frequency: 2100, q: 2.5 });
      noiseBurst({ ...v, at: v.at + 0.09 }, { duration: 0.09, gain: 0.5, frequency: 1500, q: 2 });
      tone(v, { frequency: 150, duration: 0.1, gain: 0.14, type: 'triangle', delay: 0.09 });
    },
  },

  /** Heavy timber settling into place. */
  buildComplete: {
    channel: 'game',
    gain: 0.6,
    render: (v) => {
      noiseBurst(v, { duration: 0.18, gain: 0.4, frequency: 700, q: 0.9, sweepTo: 260, curve: 0.8 });
      tone(v, { frequency: 130, duration: 0.22, gain: 0.22, type: 'triangle', slideTo: 92 });
      tone(v, { frequency: 196, duration: 0.18, gain: 0.1, type: 'triangle', delay: 0.05 });
    },
  },

  /** Hammer on anvil. Rings, but iron rings rather than bells. */
  train: {
    channel: 'game',
    gain: 0.5,
    render: (v) => {
      noiseBurst(v, { duration: 0.06, gain: 0.45, frequency: vary(3200), q: 6 });
      tone(v, { frequency: vary(880), duration: 0.24, gain: 0.09, type: 'triangle' });
      tone(v, { frequency: vary(1320), duration: 0.18, gain: 0.05, type: 'sine' });
    },
  },

  /** A drum and the swell of boots leaving the gate. */
  march: {
    channel: 'game',
    gain: 0.6,
    render: (v) => {
      tone(v, { frequency: 84, duration: 0.2, gain: 0.3, type: 'sine', slideTo: 58 });
      noiseBurst(v, { duration: 0.42, gain: 0.22, frequency: 420, q: 0.5, sweepTo: 1200, curve: 0.55 });
      tone(v, { frequency: 78, duration: 0.18, gain: 0.22, type: 'sine', delay: 0.24, slideTo: 56 });
    },
  },

  /** A low horn, two notes rising. Held short so it never becomes a fanfare. */
  victory: {
    channel: 'game',
    gain: 0.5,
    render: (v) => {
      tone(v, { frequency: 196, duration: 0.34, gain: 0.16, type: 'sawtooth' });
      tone(v, { frequency: 294, duration: 0.46, gain: 0.14, type: 'sawtooth', delay: 0.2 });
      noiseBurst(v, { duration: 0.5, gain: 0.06, frequency: 900, q: 0.4, curve: 0.5 });
    },
  },

  /** The same horn, falling. */
  defeat: {
    channel: 'game',
    gain: 0.5,
    render: (v) => {
      tone(v, { frequency: 196, duration: 0.4, gain: 0.16, type: 'sawtooth' });
      tone(v, { frequency: 147, duration: 0.6, gain: 0.15, type: 'sawtooth', delay: 0.22, slideTo: 138 });
    },
  },

  /** Coins into a palm. A short cluster of bright metal. */
  coin: {
    channel: 'game',
    gain: 0.4,
    render: (v) => {
      for (let i = 0; i < 3; i++) {
        noiseBurst(
          { ...v, at: v.at + i * 0.035 },
          { duration: 0.07, gain: 0.22, frequency: vary(4200, 0.2), q: 9 },
        );
      }
    },
  },

  /** Picking something up on the map or the plot. */
  select: {
    channel: 'ui',
    gain: 0.34,
    throttleMs: 40,
    render: (v) => {
      noiseBurst(v, { duration: 0.04, gain: 0.32, frequency: vary(2200), q: 2 });
      tone(v, { frequency: vary(420), duration: 0.05, gain: 0.07, type: 'triangle' });
    },
  },

  /** Falling into the village: a rush inward, ending on solid ground. */
  enterVillage: {
    channel: 'game',
    gain: 0.55,
    render: (v) => {
      noiseBurst(v, { duration: 0.5, gain: 0.24, frequency: 300, q: 0.5, sweepTo: 2200, curve: 0.5 });
      tone(v, { frequency: 70, duration: 0.3, gain: 0.24, type: 'sine', slideTo: 160, delay: 0.05 });
      noiseBurst({ ...v, at: v.at + 0.44 }, { duration: 0.16, gain: 0.3, frequency: 600, q: 0.9, sweepTo: 240 });
    },
  },

  /** Pulling back out. The same gesture reversed. */
  leaveVillage: {
    channel: 'game',
    gain: 0.5,
    render: (v) => {
      noiseBurst(v, { duration: 0.42, gain: 0.22, frequency: 2000, q: 0.5, sweepTo: 320, curve: 0.6 });
      tone(v, { frequency: 150, duration: 0.32, gain: 0.18, type: 'sine', slideTo: 68 });
    },
  },
};
